// Dok 08 – Inventering av farligt material (asbest, PCB, kvarts m.m.) med provpunkter,
// saneringsåtgärder och avfallsdokumentation. AFS 2023:10 asbest, AFS 2023:12 kemiska risker.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { Biohazard, Plus, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { generateHazmatSurveyWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Survey {
  id: string;
  status: string;
  surveyor: string | null;
  survey_date: string | null;
  lab_name: string | null;
  notes: string | null;
}

interface Item {
  id: string;
  material: string;
  location: string | null;
  sample_ref: string | null;
  result: string;
  quantity: string | null;
  action: string | null;
  responsible: string | null;
  permit_ref: string | null;
  waste_doc_ref: string | null;
  receiver: string | null;
  status: string;
}

const RESULTS: { value: string; label: string }[] = [
  { value: "misstanke", label: "Misstanke – ej provtaget" },
  { value: "positiv", label: "Påvisat – farligt material" },
  { value: "negativ", label: "Provtaget – inget farligt material" },
];

const STATUSES: { value: string; label: string }[] = [
  { value: "open", label: "Ej åtgärdat" },
  { value: "in_progress", label: "Sanering pågår" },
  { value: "done", label: "Sanerat och bortforslat" },
];

const HazmatSurvey: React.FC<Props> = ({ projectId }) => {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("hazmat_surveys")
      .select("id, status, surveyor, survey_date, lab_name, notes")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1);
    const s = (data?.[0] as Survey | undefined) ?? null;
    setSurvey(s);
    if (s) {
      const { data: rows } = await supabase
        .from("hazmat_items")
        .select(
          "id, material, location, sample_ref, result, quantity, action, responsible, permit_ref, waste_doc_ref, receiver, status"
        )
        .eq("survey_id", s.id)
        .order("created_at", { ascending: true });
      setItems((rows as Item[] | null) ?? []);
    } else {
      setItems([]);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("hazmat_surveys")
      .insert({
        project_id: projectId,
        name: "Inventering farligt material",
        created_by: auth.user?.id ?? null,
      });
    if (error) return toast.error("Kunde inte skapa inventeringen");
    load();
  };

  const patchSurvey = async (patch: Partial<Survey>) => {
    if (!survey) return;
    setSurvey({ ...survey, ...patch });
    const { error } = await supabase.from("hazmat_surveys").update(patch).eq("id", survey.id);
    if (error) toast.error("Kunde inte spara");
  };

  const addItem = async () => {
    if (!survey) return;
    const { error } = await supabase.from("hazmat_items").insert({
      survey_id: survey.id,
      project_id: projectId,
      material: "",
    });
    if (error) return toast.error("Kunde inte lägga till raden");
    load();
  };

  const patchItem = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("hazmat_items").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara raden");
  };

  const removeItem = async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await supabase.from("hazmat_items").delete().eq("id", id);
  };

  const positives = items.filter((i) => i.result === "positiv");
  const unresolved = positives.filter((i) => i.status !== "done");
  const suspicions = items.filter((i) => i.result === "misstanke");

  if (loading) return <p className="py-10 text-center text-muted-foreground">Laddar…</p>;

  const exportWord = async () => {
    try {
      await generateHazmatSurveyWordDocument(projectId, survey ?? {}, items);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="08"
        icon={Biohazard}
        title="Farligt material och sanering"
        description="Inventering av asbest, PCB, kvarts och annat farligt material före rivning och ombyggnad, med provpunkter, saneringsåtgärder och kvitto på var avfallet tagit vägen."
        legalRef="AFS 2023:10, AFS 2023:12"
        projectId={projectId}
        exportHistory={{ documentType: "hazmat_survey" }}
        meta={
          survey ? (
            <Badge variant={unresolved.length ? "destructive" : "secondary"}>
              {items.length} rader · {unresolved.length} osanerade
            </Badge>
          ) : undefined
        }
        actions={
          survey ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={exportWord}>
                <FileDown className="mr-1 h-4 w-4" /> Exportera Dok 08
              </Button>
              <Button size="sm" variant="outline" onClick={addItem}>
                <Plus className="mr-1 h-4 w-4" /> Lägg till rad
              </Button>
            </div>
          ) : (
            <Button onClick={create}>
              <Plus className="mr-1 h-4 w-4" /> Skapa inventering
            </Button>
          )
        }
      />

      {!survey && (
        <Card>
          <CardContent className="space-y-3 py-12 text-center">
            <Biohazard className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Innan rivning eller ombyggnad i hus byggt före 1980 ska farligt material inventeras.
              Hittas asbest krävs tillstånd från Arbetsmiljöverket och utbildad personal innan
              arbetet får starta.
            </p>
            <Button onClick={create}>Skapa inventering</Button>
          </CardContent>
        </Card>
      )}

      {survey && (
        <>
          {(unresolved.length > 0 || suspicions.length > 0) && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Arbetet får inte starta i berörda ytor</AlertTitle>
              <AlertDescription className="text-sm">
                {unresolved.length > 0 &&
                  `${unresolved.length} påvisade fynd är inte sanerade. `}
                {suspicions.length > 0 &&
                  `${suspicions.length} misstänkta material är inte provtagna. `}
                Rivning i dessa ytor kräver provsvar, tillstånd och utbildad personal först.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Om inventeringen</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Utförd av</Label>
                <Input
                  value={survey.surveyor ?? ""}
                  onChange={(e) => patchSurvey({ surveyor: e.target.value })}
                  placeholder="Namn och företag"
                />
              </div>
              <div>
                <Label>Datum</Label>
                <Input
                  type="date"
                  value={survey.survey_date ?? ""}
                  onChange={(e) => patchSurvey({ survey_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Analyslaboratorium</Label>
                <Input
                  value={survey.lab_name ?? ""}
                  onChange={(e) => patchSurvey({ lab_name: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Anteckningar</Label>
                <Textarea
                  rows={2}
                  value={survey.notes ?? ""}
                  onChange={(e) => patchSurvey({ notes: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Fynd och åtgärder</CardTitle>
              <Button size="sm" variant="outline" onClick={addItem}>
                <Plus className="mr-1 h-4 w-4" /> Lägg till rad
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground">Inga rader ännu.</p>
              )}
              {items.map((i) => (
                <div key={i.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-3">
                  <div>
                    <Label>Material</Label>
                    <Input
                      value={i.material}
                      onChange={(e) => patchItem(i.id, { material: e.target.value })}
                      placeholder="T.ex. golvmatta med asbestlim"
                    />
                  </div>
                  <div>
                    <Label>Plats</Label>
                    <Input
                      value={i.location ?? ""}
                      onChange={(e) => patchItem(i.id, { location: e.target.value })}
                      placeholder="T.ex. plan 2, sal 214"
                    />
                  </div>
                  <div>
                    <Label>Provnummer</Label>
                    <Input
                      value={i.sample_ref ?? ""}
                      onChange={(e) => patchItem(i.id, { sample_ref: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Provsvar</Label>
                    <Select
                      value={i.result}
                      onValueChange={(v) => patchItem(i.id, { result: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RESULTS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Mängd</Label>
                    <Input
                      value={i.quantity ?? ""}
                      onChange={(e) => patchItem(i.id, { quantity: e.target.value })}
                      placeholder="T.ex. 120 m²"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={i.status}
                      onValueChange={(v) => patchItem(i.id, { status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Åtgärd och metod</Label>
                    <Input
                      value={i.action ?? ""}
                      onChange={(e) => patchItem(i.id, { action: e.target.value })}
                      placeholder="T.ex. sanering i undertrycksavskiljd zon"
                    />
                  </div>
                  <div>
                    <Label>Ansvarig</Label>
                    <Input
                      value={i.responsible ?? ""}
                      onChange={(e) => patchItem(i.id, { responsible: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Tillstånd (AV)</Label>
                    <Input
                      value={i.permit_ref ?? ""}
                      onChange={(e) => patchItem(i.id, { permit_ref: e.target.value })}
                      placeholder="Diarienummer"
                    />
                  </div>
                  <div>
                    <Label>Transportdokument</Label>
                    <Input
                      value={i.waste_doc_ref ?? ""}
                      onChange={(e) => patchItem(i.id, { waste_doc_ref: e.target.value })}
                      placeholder="Nummer på transportdokument"
                    />
                  </div>
                  <div>
                    <Label>Mottagningsanläggning</Label>
                    <Input
                      value={i.receiver ?? ""}
                      onChange={(e) => patchItem(i.id, { receiver: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-3 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removeItem(i.id)}>
                      <Trash2 className="mr-1 h-4 w-4" /> Ta bort
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HazmatSurvey;
